/**
 * use-auto-currency-rates.ts — tự cập nhật tỷ giá NGẦM, không cần UI.
 * Khi app có ví ngoại tệ (≠ VND) mà tỷ giá thiếu hoặc cũ hơn 24h → fetch
 * open.er-api.com một lần và lưu qua setCurrencyRate. Lỗi mạng → im lặng,
 * giữ tỷ giá cũ (convertToVnd đã có cờ missingRate cho UI cảnh báo).
 * Chỉ thử 1 lần mỗi phiên cho mỗi tập tiền tệ — thêm ví ngoại tệ mới sẽ
 * kích hoạt fetch ngay vì key đổi.
 */
import { useEffect, useRef } from "react";
import type { LifeOsApi } from "../lib/life-os-contract";
import { fetchRatesToVnd } from "../lib/currency-rates-api";

const STALE_MS = 24 * 60 * 60 * 1000;

export function useAutoCurrencyRates(api: LifeOsApi) {
  const attemptedKey = useRef("");
  const codes = [...new Set(
    api.state.accounts
      .map((a) => (a.currency ?? "VND").trim().toUpperCase())
      .filter((c) => c && c !== "VND"),
  )].sort();
  const key = codes.join(",");
  // Ref để effect đọc state/actions mới nhất mà không cần đưa vào deps
  // (deps chỉ [key] — tránh fetch lại mỗi lần sync echo đổi tham chiếu state).
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    if (!key) return; // toàn VND — không có gì để quy đổi
    const now = Date.now();
    const currentCodes = key.split(",");
    const stale = currentCodes.some((code) => {
      const saved = apiRef.current.state.currencyRates.find((r) => r.id === code);
      return !saved || !(now - new Date(saved.updatedAt).getTime() < STALE_MS);
    });
    if (!stale || attemptedKey.current === key) return;
    attemptedKey.current = key;
    fetchRatesToVnd(currentCodes)
      .then((rates) => {
        for (const [code, rate] of Object.entries(rates)) {
          apiRef.current.actions.setCurrencyRate(code, rate);
        }
      })
      .catch(() => {
        // Im lặng theo thiết kế — offline/API hỏng thì dùng tỷ giá đã lưu.
      });
  }, [key]);
}
